import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { effectiveCardValue, GAME_CONFIG, priceBounds, saleFee, saleNet, systemBuyback } from '@anidex/shared/economy';
import type { CardOwnershipDTO, CharacterDTO } from '@anidex/shared/types';
import { AnimaAmount } from '../../components/Brand';
import { Sheet } from '../../components/Sheet';
import { useToasts } from '../../components/Toasts';
import { errorMessage } from '../../services/api';
import { formatNumber } from '../../services/format';
import { useCardSheet } from '../../hooks/useCardSheet';
import { useCreateAuction, useCreateListing, useSellToSystem } from '../../services/queries';

export function SellSheet({
  open,
  onClose,
  character,
  ownership,
}: {
  open: boolean;
  onClose: () => void;
  character: CharacterDTO;
  ownership: CardOwnershipDTO;
}) {
  const { t } = useTranslation('market');
  const cardValue = effectiveCardValue(character.value, ownership.upgradeLevel);
  const bounds = priceBounds(cardValue);
  const buyback = systemBuyback(cardValue);
  const [mode, setMode] = useState<'listing' | 'auction' | 'system'>('listing');
  const [price, setPrice] = useState(() => String(Math.min(bounds.max, Math.max(bounds.min, cardValue))));
  const minPrice = cardValue;
  const maxPrice = cardValue * 6;
  const [buyout, setBuyout] = useState('');
  const [duration, setDuration] = useState(6);
  const createListing = useCreateListing();
  const createAuction = useCreateAuction();
  const sellToSystem = useSellToSystem();
  const sheet = useCardSheet();
  const toasts = useToasts();
  const ids = useId();

  const inBounds = (value: number) => Number.isInteger(value) && value >= bounds.min && value <= bounds.max;
  const amount = Number(price);
  const buyoutAmount = buyout === '' ? null : Number(buyout);
  const priceValid = inBounds(amount);
  const buyoutValid = buyoutAmount === null || (inBounds(buyoutAmount) && buyoutAmount > amount + GAME_CONFIG.market.auctionIncrementBase);
  const valid = mode === 'system' ? buyback.net >= 1 : priceValid && (mode === 'listing' || buyoutValid);
  const pending = createListing.isPending || createAuction.isPending || sellToSystem.isPending;

  const submit = () => {
    const key = crypto.randomUUID().replace(/-/g, '').slice(0, 24);
    const handlers = {
      onSuccess: () => {
        toasts.push({
          tone: 'success' as const,
          title: mode === 'listing' ? t('sell.listed') : t('sell.launched'),
          description: character.name,
        });
        onClose();
      },
      onError: (error: unknown) => toasts.push({ tone: 'error', title: t('sell.listFailed'), description: errorMessage(error) }),
    };
    if (mode === 'system') {
      sellToSystem.mutate(
        { characterId: character.id, idempotencyKey: key },
        {
          onSuccess: (result) => {
            toasts.push({
              tone: 'success',
              title: t('sell.boughtTitle'),
              description: t('sell.bought', { name: character.name, amount: formatNumber(result.net) }),
            });
            onClose();
            sheet.close();
          },
          onError: handlers.onError,
        },
      );
      return;
    }
    if (mode === 'listing') {
      createListing.mutate({ characterId: character.id, price: amount, idempotencyKey: key }, handlers);
    } else {
      createAuction.mutate(
        {
          characterId: character.id,
          startingPrice: amount,
          buyoutPrice: buyoutAmount ?? undefined,
          durationHours: duration,
          idempotencyKey: key,
        },
        handlers,
      );
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={t('sell.title', { name: character.name })} showTitle>
      <form
        className="market-sheet"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="segmented segmented--wide" role="radiogroup" aria-label={t('sell.kind')}>
          <button type="button" role="radio" aria-checked={mode === 'listing'} onClick={() => setMode('listing')}>
            {t('sell.fixed')}
          </button>
          <button type="button" role="radio" aria-checked={mode === 'auction'} onClick={() => setMode('auction')}>
            {t('sell.auction')}
          </button>
          <button type="button" role="radio" aria-checked={mode === 'system'} onClick={() => setMode('system')}>
            {t('sell.system')}
          </button>
        </div>

        {mode !== 'system' && (
          <>
            <div className="field">
              <label htmlFor={`${ids}-price`}>{mode === 'listing' ? t('sell.price') : t('sell.start')}</label>
              <input
                id={`${ids}-price`}
                type="number"
                inputMode="numeric"
                min={minPrice}
                max={maxPrice}
                step={1}
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                aria-invalid={!priceValid}
                aria-describedby={`${ids}-price-hint`}
              />
              <p id={`${ids}-price-hint`} className="field__hint">
                {t('sell.range', { min: formatNumber(minPrice), max: formatNumber(maxPrice) })}
              </p>
            </div>

            {mode === 'auction' && (
              <>
                <div className="field">
                  <label htmlFor={`${ids}-buyout`}>{t('sell.buyout')}</label>
                  <input
                    id={`${ids}-buyout`}
                    type="number"
                    inputMode="numeric"
                    min={amount + GAME_CONFIG.market.auctionIncrementBase + 1}
                    max={bounds.max}
                    step={1}
                    placeholder={t('sell.none')}
                    value={buyout}
                    onChange={(event) => setBuyout(event.target.value)}
                    aria-invalid={!buyoutValid}
                    aria-describedby={`${ids}-buyout-hint`}
                  />
                  <p id={`${ids}-buyout-hint`} className="field__hint">
                    {t('sell.buyoutHint')}
                  </p>
                </div>
                <div className="field">
                  <span id={`${ids}-duration`} className="field__label">
                    {t('sell.duration')}
                  </span>
                  <div className="segmented segmented--wide" role="radiogroup" aria-labelledby={`${ids}-duration`}>
                    {GAME_CONFIG.market.auctionDurationsHours.map((hours) => (
                      <button key={hours} type="button" role="radio" aria-checked={duration === hours} onClick={() => setDuration(hours)}>
                        {t('sell.hours', { hours })}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <dl className="facts">
          <div>
            <dt>{t('sell.value', { level: ownership.upgradeLevel })}</dt>
            <dd>
              <AnimaAmount value={cardValue} size={16} />
            </dd>
          </div>
          {mode === 'system' ? (
            <>
              <div>
                <dt>{t('sell.buyback', { percent: GAME_CONFIG.market.systemBuybackPercent })}</dt>
                <dd>
                  <AnimaAmount value={buyback.price} size={16} />
                </dd>
              </div>
              <div>
                <dt>{t('sell.jackpot', { percent: GAME_CONFIG.market.systemBuybackPotPercent })}</dt>
                <dd>
                  <AnimaAmount value={buyback.fee} size={16} />
                </dd>
              </div>
              <div>
                <dt>{t('sell.receive')}</dt>
                <dd>
                  <AnimaAmount value={buyback.net} size={16} />
                </dd>
              </div>
            </>
          ) : (
            <>
              <div>
                <dt>{t('sell.fee', { percent: GAME_CONFIG.market.saleFeePercent })}</dt>
                <dd>
                  <AnimaAmount value={priceValid ? saleFee(amount) : 0} size={16} />
                </dd>
              </div>
              <div>
                <dt>{mode === 'listing' ? t('sell.net') : t('sell.netLeast')}</dt>
                <dd>
                  <AnimaAmount value={priceValid ? saleNet(amount) : 0} size={16} />
                </dd>
              </div>
            </>
          )}
        </dl>

        <p className="hint">
          {mode === 'system'
            ? t('sell.systemHint', {
                percent: GAME_CONFIG.market.systemBuybackPercent,
                pot: GAME_CONFIG.market.systemBuybackPotPercent,
              })
            : t('sell.feeHint', { level: ownership.upgradeLevel })}
          {ownership.fragments > 0 && <> {t('sell.fragments', { amount: formatNumber(ownership.fragments) })}</>}
        </p>
        {mode === 'system' && buyback.net < 1 && <p className="form-error">{t('sell.tooCheap')}</p>}
        {ownership.lock && <p className="form-error">{t('sell.locked')}</p>}

        <button
          type="submit"
          className="button button--primary button--large button--block"
          disabled={Boolean(ownership.lock) || !valid || pending}
        >
          {mode === 'system' ? t('sell.toSystem') : mode === 'listing' ? t('sell.list') : t('sell.launch')}
        </button>
      </form>
    </Sheet>
  );
}
